var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import Downshift from 'downshift';
import { css } from '@emotion/react';
import { useRef } from 'react';
import { Icon } from '../../display/Icon';
import { FloatingMenu } from '../../forms/FloatingMenu';
import { Input } from '../../forms/Input';
import { Colors } from '../../../theme';
import { camelCaseAriaAttrs } from '../../../utils/formatAriaAttrKeys';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { omit } from '../../../utils/omit';
const isDisabled = (props) => {
    return props.isDisabled || props.items.length === 0;
};
const Styles = labelStyles('Dropdown', {
    root: css({
        position: 'relative',
    }),
    buttonWrap: (props) => ({
        cursor: isDisabled(props) ? 'default' : 'pointer',
    }),
    button: css({
        maxWidth: '100%',
        overflow: 'hidden',
    }),
    buttonText: css({
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    }),
    placeholder: css({
        color: Colors.neutral600,
    }),
});
const buildAriaLabel = ({ label, value, placeholder, }) => {
    return [value, label, !value && placeholder].filter(Boolean).join(', ');
};
const defaultProps = {
    size: 'small',
    getLabelForItem: (item) => item.label,
    renderFloatingMenu: (props) => isolateComponent(FloatingMenu, Object.assign({}, props)),
    menuAriaLabel: 'List of items',
};
export function Dropdown(_props) {
    const props = Object.assign(Object.assign({}, defaultProps), _props);
    const parentRef = useRef(null);
    const itemToString = (item) => {
        return item ? props.getLabelForItem(item) : '';
    };
    const onChangeNoNull = (selection) => {
        var _a;
        if (selection) {
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, selection);
        }
    };
    const onSelectNoNull = (selection) => {
        var _a;
        if (selection) {
            (_a = props.onSelect) === null || _a === void 0 ? void 0 : _a.call(props, selection);
        }
    };
    const stateReducer = (_, changes) => {
        switch (changes.type) {
            case Downshift.stateChangeTypes.keyDownEscape:
                return {
                    isOpen: false,
                };
            default:
                return changes;
        }
    };
    const renderButtonForItem = props.renderButtonForItem || props.getLabelForItem;
    return (isolateComponent(Downshift, { initialSelectedItem: props.initialSelectedItem, inputId: props.inputId, itemCount: props.items.length, itemToString: itemToString, onChange: onChangeNoNull, onSelect: onSelectNoNull, stateReducer: stateReducer, selectedItem: props.selectedItem }, ({ clearSelection, closeMenu, getInputProps, getItemProps, getMenuProps, getRootProps, highlightedIndex, isOpen, openMenu, selectItem, selectedItem, setHighlightedIndex, }) => {
        const hasSelectedItem = !!selectedItem;
        const renderIcon = () => typeof props.renderIcon === 'function'
            ? props.renderIcon(selectedItem)
            : undefined;
        const rootProps = omit(getRootProps(), ['aria-labelledby']);
        const onBlur = (e) => {
            if (props.onBlur) {
                const meta = {
                    didBlurWithin: parentRef.current
                        ? parentRef.current.contains(e.relatedTarget)
                        : false,
                    closeMenu,
                };
                props.onBlur(e, meta);
            }
            else {
                closeMenu();
            }
        };
        const { onChange, onClick } = props, propsNoOnChangeOrOnClick = __rest(props, ["onChange", "onClick"]);
        return (isolateComponent("div", Object.assign({ "aria-label": props.menuAriaLabel, "aria-labelledby": props.inputId, css: Styles.root }, rootProps, { onBlur: onBlur, ref: parentRef }),
            isolateComponent("div", { css: Styles.buttonWrap(props) },
                isolateComponent(Input, Object.assign({ onClick: () => {
                        if (isOpen === true) {
                            closeMenu();
                        }
                        else if (!isDisabled(props)) {
                            openMenu();
                        }
                        if (props.onClick)
                            props.onClick();
                    } }, propsNoOnChangeOrOnClick, camelCaseAriaAttrs(getInputProps()), { canClickIconAfter: false, id: props.inputId, isDisabled: isDisabled(props), renderIconAfter: () => isolateComponent(Icon, { name: "arrowDropdown", size: 24 }), renderIconBefore: renderIcon }), (inputProps) => (isolateComponent("button", Object.assign({}, {
                    'aria-activedescendant': inputProps['aria-activedescendant'],
                    'aria-invalid': inputProps['aria-invalid'],
                }, { "aria-controls": inputProps['aria-controls'], "aria-expanded": isOpen ? 'true' : 'false', "aria-haspopup": "true", "aria-label": buildAriaLabel({
                        label: inputProps['aria-label'] || '',
                        placeholder: props.placeholder,
                        value: hasSelectedItem
                            ? props.getLabelForItem(selectedItem)
                            : undefined,
                    }), css: [inputProps.css, Styles.button], disabled: inputProps.disabled, id: inputProps.id, onBlur: onBlur, onFocus: props.onFocus, onKeyDown: inputProps.onKeyDown, type: "button" }), hasSelectedItem ? (isolateComponent("span", { css: Styles.buttonText }, renderButtonForItem(selectedItem))) : (isolateComponent("span", { css: [Styles.buttonText, Styles.placeholder] }, props.placeholder)))))),
            isOpen &&
                props.renderFloatingMenu({
                    ariaLabel: props.menuAriaLabel,
                    clearSelection,
                    closeMenu,
                    items: props.items,
                    highlightedIndex,
                    getKeyForItem: props.getKeyForItem,
                    getLabelForItem: props.getLabelForItem,
                    getItemProps,
                    getMenuProps,
                    getInputProps,
                    renderIcon: props.renderIcon,
                    selectItem,
                    selectedItem,
                    setHighlightedIndex,
                })));
    }));
}
Dropdown.defaultProps = defaultProps;
//# sourceMappingURL=Dropdown.js.map
// @zapier/design-system@5.32.5 (2004a9d50)
